<script>
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import CampaignContactGroupsAPI from 'dashboard/api/campaignContactGroups';

export default {
  name: 'ImportContactGroupCsvModal',
  emits: ['close', 'success'],
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      name: '',
      description: '',
      csvFile: null,
      loading: false,
      dragOver: false,
    };
  },
  validations() {
    return {
      name: { required },
      csvFile: { required },
    };
  },
  methods: {
    handleFileSelect(event) {
      const file = event.target.files[0];
      this.processFile(file);
    },
    handleFileDrop(event) {
      this.dragOver = false;
      const file = event.dataTransfer.files[0];
      this.processFile(file);
    },
    processFile(file) {
      if (!file) return;
      
      // Aceita arquivos com extensão .csv ou MIME type text/csv
      // No Windows, CSV pode ter MIME type diferente (application/vnd.ms-excel, vazio, etc)
      const isCSV = file.name.toLowerCase().endsWith('.csv') || 
                    file.type === 'text/csv' || 
                    file.type === 'application/vnd.ms-excel' ||
                    file.type === 'application/csv';
      
      if (isCSV) {
        this.csvFile = file;
        this.v$.csvFile.$touch();
      } else {
        useAlert(this.$t('CAMPAIGN.ADD.CONTACTS.GROUP.CSV_INVALID_TYPE'));
      }
    },
    removeFile() {
      this.csvFile = null;
      this.$refs.fileInput.value = '';
    },
    async onSubmit() {
      this.v$.$touch();
      if (this.v$.$invalid) {
        return;
      }

      this.loading = true;
      try {
        const formData = new FormData();
        formData.append('csv_file', this.csvFile);
        formData.append('name', this.name.trim());
        if (this.description.trim()) {
          formData.append('description', this.description.trim());
        }

        const { data } = await CampaignContactGroupsAPI.importCsv(formData);
        
        const successMsg = this.$t('CAMPAIGN.ADD.CONTACTS.GROUP.CSV_SUCCESS', {
          imported: data.imported_count,
          rejected: data.rejected_count,
        });
        useAlert(successMsg);
        
        this.$emit('success', data.group);
        this.$emit('close');
      } catch (error) {
        const message =
          error?.response?.data?.error || this.$t('CAMPAIGN.ADD.CONTACTS.GROUP.CSV_ERROR');
        useAlert(message);
      } finally {
        this.loading = false;
      }
    },
    onCancel() {
      this.$emit('close');
    },
    downloadSampleCSV(type) {
      let csvContent = '';
      let filename = '';

      if (type === 'minimal') {
        // CSV mínimo com apenas os campos obrigatórios
        csvContent = `name,phone_number
Joao Silva,+5511999999999
Maria Santos,+5511988888888
Pedro Oliveira,+5521977777777
Ana Costa,+5511966666666
Carlos Ferreira,+5541955555555`;
        filename = 'exemplo_contatos_minimo.csv';
      } else {
        // CSV completo com todos os campos
        csvContent = `name,phone_number,email,identifier,company_name,city
João Silva,+5511999999999,joao.silva@example.com,CLI001,Tech Solutions,Sao Paulo
Maria Santos,+5511888888888,maria.santos@example.com,CLI002,Digital Marketing,Rio de Janeiro
Pedro Oliveira,+5511777777777,pedro.oliveira@example.com,CLI003,E-commerce Plus,Belo Horizonte
Ana Costa,+5511666666666,ana.costa@example.com,CLI004,Startup Innovation,Curitiba
Carlos Ferreira,+5511555555555,carlos.ferreira@example.com,CLI005,Consulting Group,Porto Alegre`;
        filename = 'exemplo_contatos_completo.csv';
      }

      // Cria um blob e faz o download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
  },
};
</script>

<template>
  <div class="flex flex-col gap-4 p-4">
    <woot-modal-header
      :header-title="$t('CAMPAIGN.ADD.CONTACTS.GROUP.CSV_TITLE')"
      :header-content="$t('CAMPAIGN.ADD.CONTACTS.GROUP.CSV_DESCRIPTION')"
    />

    <div class="flex flex-col gap-2">
      <label class="woot-form-label" for="contact-group-name">
        {{ $t('CAMPAIGN.ADD.CONTACTS.GROUP.NAME_LABEL') }}
      </label>
      <input
        id="contact-group-name"
        v-model.trim="name"
        type="text"
        class="woot-input"
        :placeholder="$t('CAMPAIGN.ADD.CONTACTS.GROUP.NAME_PLACEHOLDER')"
        :class="{ error: v$.name.$error }"
        @blur="v$.name.$touch"
      />
      <span v-if="v$.name.$error" class="message text-sm">
        {{ $t('CAMPAIGN.ADD.CONTACTS.GROUP.NAME_ERROR') }}
      </span>
    </div>

    <div class="flex flex-col gap-2">
      <label class="woot-form-label" for="contact-group-description">
        {{ $t('CAMPAIGN.ADD.CONTACTS.GROUP.DESCRIPTION_LABEL') }}
      </label>
      <textarea
        id="contact-group-description"
        v-model.trim="description"
        class="woot-input"
        rows="2"
        :placeholder="$t('CAMPAIGN.ADD.CONTACTS.GROUP.DESCRIPTION_PLACEHOLDER')"
      />
    </div>

    <div class="flex flex-col gap-2">
      <label class="woot-form-label">
        {{ $t('CAMPAIGN.ADD.CONTACTS.GROUP.CSV_FILE_LABEL') }}
      </label>
      
      <div
        v-if="!csvFile"
        class="border-2 border-dashed rounded-lg p-6 text-center transition-colors"
        :class="{
          'border-blue-300 bg-blue-50 dark:bg-blue-900/20': dragOver,
          'border-slate-300 dark:border-slate-600 hover:border-slate-400': !dragOver,
          'border-red-500': v$.csvFile.$error
        }"
        @dragover.prevent="dragOver = true"
        @dragleave.prevent="dragOver = false"
        @drop.prevent="handleFileDrop"
        @click="$refs.fileInput.click()"
      >
        <i class="icon ion-document-text text-4xl text-slate-400 dark:text-slate-500 mb-2" />
        <p class="text-sm text-slate-600 dark:text-slate-300 mb-1">
          {{ $t('CAMPAIGN.ADD.CONTACTS.GROUP.CSV_DROP_ZONE') }}
        </p>
        <p class="text-xs text-slate-500 dark:text-slate-400">
          {{ $t('CAMPAIGN.ADD.CONTACTS.GROUP.CSV_FILE_HINT') }}
        </p>
        <input
          ref="fileInput"
          type="file"
          accept=".csv,text/csv,application/vnd.ms-excel,application/csv"
          class="hidden"
          @change="handleFileSelect"
        />
      </div>

      <div
        v-else
        class="border border-slate-300 dark:border-slate-600 rounded-lg p-4 flex items-center justify-between"
      >
        <div class="flex items-center gap-3">
          <i class="icon ion-document-text text-2xl text-green-500" />
          <div>
            <p class="text-sm font-medium text-slate-700 dark:text-slate-200">
              {{ csvFile.name }}
            </p>
            <p class="text-xs text-slate-500 dark:text-slate-400">
              {{ (csvFile.size / 1024).toFixed(2) }} KB
            </p>
          </div>
        </div>
        <button
          type="button"
          class="text-red-500 hover:text-red-600"
          @click="removeFile"
        >
          <i class="icon ion-close-round text-xl" />
        </button>
      </div>
      
      <span v-if="v$.csvFile.$error" class="message text-sm">
        {{ $t('CAMPAIGN.ADD.CONTACTS.GROUP.CSV_FILE_ERROR') }}
      </span>
    </div>

    <div class="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-700 p-3">
      <p class="text-xs font-semibold text-yellow-800 dark:text-yellow-300 mb-1">
        {{ $t('CAMPAIGN.ADD.CONTACTS.GROUP.CSV_FORMAT_TITLE') }}
      </p>
      <p class="text-xs text-yellow-700 dark:text-yellow-300 leading-relaxed mb-2">
        {{ $t('CAMPAIGN.ADD.CONTACTS.GROUP.CSV_FORMAT_DESCRIPTION') }}
      </p>
      
      <!-- Botões de Download de Exemplo -->
      <div class="flex gap-2 mb-3">
        <button
          type="button"
          class="flex-1 px-3 py-2 text-xs font-medium text-yellow-800 dark:text-yellow-300 bg-yellow-100 dark:bg-yellow-900/40 border border-yellow-300 dark:border-yellow-600 rounded hover:bg-yellow-200 dark:hover:bg-yellow-900/60 transition-colors flex items-center justify-center gap-1"
          @click="downloadSampleCSV('minimal')"
        >
          <i class="icon ion-arrow-down-a text-sm" />
          {{ $t('CAMPAIGN.ADD.CONTACTS.GROUP.CSV_DOWNLOAD_MINIMAL') }}
        </button>
        <button
          type="button"
          class="flex-1 px-3 py-2 text-xs font-medium text-yellow-800 dark:text-yellow-300 bg-yellow-100 dark:bg-yellow-900/40 border border-yellow-300 dark:border-yellow-600 rounded hover:bg-yellow-200 dark:hover:bg-yellow-900/60 transition-colors flex items-center justify-center gap-1"
          @click="downloadSampleCSV('complete')"
        >
          <i class="icon ion-arrow-down-a text-sm" />
          {{ $t('CAMPAIGN.ADD.CONTACTS.GROUP.CSV_DOWNLOAD_COMPLETE') }}
        </button>
      </div>

      <p class="text-xs font-semibold text-yellow-800 dark:text-yellow-300 mb-2">
        {{ $t('CAMPAIGN.ADD.CONTACTS.GROUP.CSV_REQUIRED_FIELDS') }}
      </p>
      <ul class="text-xs text-yellow-700 dark:text-yellow-300 list-disc list-inside space-y-1 mb-2">
        <li><strong>name</strong> - {{ $t('CAMPAIGN.ADD.CONTACTS.GROUP.CSV_FIELD_NAME') }} <span class="text-red-600 dark:text-red-400">*</span></li>
        <li><strong>phone_number</strong> - {{ $t('CAMPAIGN.ADD.CONTACTS.GROUP.CSV_FIELD_PHONE') }} <span class="text-red-600 dark:text-red-400">*</span></li>
      </ul>
      <p class="text-xs font-semibold text-yellow-800 dark:text-yellow-300 mb-2">
        {{ $t('CAMPAIGN.ADD.CONTACTS.GROUP.CSV_OPTIONAL_FIELDS') }}
      </p>
      <ul class="text-xs text-yellow-700 dark:text-yellow-300 list-disc list-inside space-y-1">
        <li>email - {{ $t('CAMPAIGN.ADD.CONTACTS.GROUP.CSV_FIELD_EMAIL') }}</li>
        <li>identifier - {{ $t('CAMPAIGN.ADD.CONTACTS.GROUP.CSV_FIELD_IDENTIFIER') }}</li>
        <li>company_name - {{ $t('CAMPAIGN.ADD.CONTACTS.GROUP.CSV_FIELD_COMPANY') }}</li>
        <li>city - {{ $t('CAMPAIGN.ADD.CONTACTS.GROUP.CSV_FIELD_CITY') }}</li>
      </ul>
    </div>

    <div class="flex justify-end gap-2 pt-2 border-t border-slate-200 dark:border-slate-700">
      <woot-button variant="clear" type="button" @click="onCancel">
        {{ $t('CAMPAIGN.ADD.CONTACTS.GROUP.CANCEL') }}
      </woot-button>
      <woot-button
        variant="primary"
        type="button"
        :loading="loading"
        :disabled="!csvFile || !name || loading"
        @click="onSubmit"
      >
        {{ $t('CAMPAIGN.ADD.CONTACTS.GROUP.CSV_SUBMIT') }}
      </woot-button>
    </div>
  </div>
</template>

<style scoped>
.message {
  color: var(--r-500);
  display: block;
  margin-top: 0.25rem;
}

.woot-input.error {
  border-color: var(--r-500);
}

.woot-input.error:focus {
  border-color: var(--r-500);
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
}
</style>

